import { AUDIT_ACTIONS } from '@mm/shared';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Fragment, useState } from 'react';
import { api } from '../../api/client';
import { Card } from '../../components/ui/display';
import { Input, Select } from '../../components/ui/inputs';
import { useDebounce } from '../../hooks/useDebounce';
import type { AuditLog } from '../../types/models';
import { formatDateTime } from '../../utils/format';

const show = (v: unknown) =>
  v === null || v === undefined || v === ''
    ? '—'
    : typeof v === 'object'
      ? JSON.stringify(v)
      : String(v);

/** One-line summary of a change. */
function summary(log: AuditLog): string {
  const v = log.newValues ?? {};
  if (log.action === 'APP_ACCOUNT_ASSIGNED')
    return `${show(v.account)} added to ${show(v.app)} from ${show(v.assignedOn)}`;
  if (log.action === 'APP_ACCOUNT_REMOVED')
    return `${show(v.account)} removed from ${show(v.app)} (last day ${show(v.removedOn)})`;
  const keys = Object.keys(log.newValues ?? log.oldValues ?? {});
  if (log.action === 'UPDATE' || log.action === 'STATUS_CHANGE' || log.action === 'RECALCULATE') {
    return keys
      .map((k) => `${k}: ${show(log.oldValues?.[k])} → ${show(log.newValues?.[k])}`)
      .join('; ');
  }
  return keys.length ? `${keys.length} field${keys.length === 1 ? '' : 's'}` : '';
}

/** Settings → Audit log: every change to financial and master data, with filters and full details. */
export function AuditLogPanel() {
  const [page, setPage] = useState(1);
  const [entity, setEntity] = useState('');
  const [action, setAction] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [entityId, setEntityId] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const debouncedId = useDebounce(entityId.trim());
  const entities = useQuery({
    queryKey: ['audit-logs', 'entities'],
    queryFn: () => api.get<string[]>('/audit-logs/entities'),
    staleTime: 60_000,
  });
  const filters = {
    entity,
    action,
    from,
    to,
    entityId: debouncedId.length === 36 ? debouncedId : '',
  };
  const q = useQuery({
    queryKey: ['audit-logs', filters, page],
    queryFn: () => api.list<AuditLog>('/audit-logs', { ...filters, page, pageSize: 50 }),
    placeholderData: keepPreviousData,
  });
  const set = (fn: (v: string) => void) => (v: string) => {
    fn(v);
    setPage(1);
  };
  const rows = q.data?.data ?? [];
  const meta = q.data?.meta;

  return (
    <Card
      title="Audit log"
      description="Every change to financial and master data, newest first. Click a row for the full before/after values."
      bodyClassName="p-0"
    >
      <div className="grid grid-cols-1 gap-3 border-b border-slate-100 p-4 sm:grid-cols-2 xl:grid-cols-5">
        <Select aria-label="Entity" value={entity} onChange={(e) => set(setEntity)(e.target.value)}>
          <option value="">All entities</option>
          {entities.data?.map((e) => (
            <option key={e} value={e}>
              {e}
            </option>
          ))}
        </Select>
        <Select aria-label="Action" value={action} onChange={(e) => set(setAction)(e.target.value)}>
          <option value="">All actions</option>
          {AUDIT_ACTIONS.map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </Select>
        <Input
          type="date"
          aria-label="From date"
          value={from}
          onChange={(e) => set(setFrom)(e.target.value)}
        />
        <Input
          type="date"
          aria-label="To date"
          value={to}
          onChange={(e) => set(setTo)(e.target.value)}
        />
        <Input
          aria-label="Record id"
          placeholder="Record id (paste)"
          value={entityId}
          onChange={(e) => set(setEntityId)(e.target.value)}
        />
      </div>
      <div className="relative overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-xs font-semibold tracking-wide text-slate-500 uppercase">
            <tr>
              <th className="w-8 px-2 py-3" />
              <th className="px-4 py-3 text-left">When</th>
              <th className="px-4 py-3 text-left">User</th>
              <th className="px-4 py-3 text-left">Action</th>
              <th className="px-4 py-3 text-left">Entity</th>
              <th className="px-4 py-3 text-left">Changes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((l) => {
              const expanded = open === l.id;
              const keys = [
                ...new Set([...Object.keys(l.oldValues ?? {}), ...Object.keys(l.newValues ?? {})]),
              ];
              return (
                <Fragment key={l.id}>
                  <tr
                    className="cursor-pointer hover:bg-slate-50"
                    onClick={() => setOpen(expanded ? null : l.id)}
                  >
                    <td className="px-2 py-2 text-slate-400">
                      {expanded ? (
                        <ChevronDown className="size-4" />
                      ) : (
                        <ChevronRight className="size-4" />
                      )}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">{formatDateTime(l.createdAt)}</td>
                    <td className="px-4 py-2">{l.user?.name ?? 'System'}</td>
                    <td className="px-4 py-2">
                      <code className="text-xs">{l.action}</code>
                    </td>
                    <td className="px-4 py-2">{l.entity}</td>
                    <td className="max-w-md truncate px-4 py-2 text-xs text-slate-500">
                      {summary(l)}
                    </td>
                  </tr>
                  {expanded && (
                    <tr className="bg-slate-50/60">
                      <td />
                      <td colSpan={5} className="px-4 py-3">
                        <p className="mb-2 text-xs text-slate-500">
                          Record <code>{l.entityId ?? '—'}</code>
                          {l.ip ? ` · IP ${l.ip}` : ''}
                        </p>
                        {keys.length === 0 ? (
                          <p className="text-xs text-slate-400">No field values recorded.</p>
                        ) : (
                          <table className="text-xs">
                            <thead className="text-slate-500">
                              <tr>
                                <th className="pr-6 text-left font-medium">Field</th>
                                <th className="pr-6 text-left font-medium">Before</th>
                                <th className="text-left font-medium">After</th>
                              </tr>
                            </thead>
                            <tbody>
                              {keys.map((k) => (
                                <tr key={k}>
                                  <td className="pr-6 font-mono text-slate-600">{k}</td>
                                  <td className="pr-6 text-rose-700">{show(l.oldValues?.[k])}</td>
                                  <td className="text-emerald-700">{show(l.newValues?.[k])}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        {rows.length === 0 && !q.isLoading && (
          <p className="px-5 py-6 text-sm text-slate-500">No audit entries match.</p>
        )}
      </div>
      {meta && meta.total > meta.pageSize && (
        <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3 text-sm">
          <span className="text-slate-500">
            {meta.total} entries · page {meta.page} of {Math.ceil(meta.total / meta.pageSize)}
          </span>
          <span className="flex gap-2">
            <button
              type="button"
              className="rounded-md border px-3 py-1 disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded-md border px-3 py-1 disabled:opacity-40"
              disabled={page >= Math.ceil(meta.total / meta.pageSize)}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </span>
        </div>
      )}
    </Card>
  );
}
